import { ApiURL } from '../config';

const base_api = ApiURL;

interface ApiOptions {
  method?: string;
  body?: any;
  auth?: boolean;
  signal?: AbortSignal;
}

async function callAPI(
  url: string,
  { method = 'GET', body = null, auth = true, signal }: ApiOptions = {},
): Promise<any> {
  let headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // Connection keep-alive for HTTP/1.1: reuses TCP connections, reduces latency by ~50-200ms per request
    // Modern browsers (HTTP/2) auto-multiplex on single connection, but this ensures fallback behavior
    Connection: 'keep-alive',
  };

  if (auth) {
    const userJson = localStorage.getItem('user');
    if (!userJson) {
      throw new Error('Invalid user');
    }

    const user = JSON.parse(userJson);
    if (!user.token) {
      throw new Error('Invalid token');
    }

    headers.Authorization = `Bearer ${user.token}`;
  }

  const fetchPromise = fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : null,
    signal,
  })
    .then(async (res) => {
      if (!res.ok) {
        throw new Error(`API Error: ${res.status} ${res.statusText}`);
      }
      return res.json();
    })
    .finally(() => {});

  return fetchPromise;
}

async function get(
  url: string,
  auth = true,
  signal?: AbortSignal,
): Promise<any> {
  return callAPI(url, { auth, signal });
}

async function post(
  url: string,
  body: any,
  auth = true,
  signal?: AbortSignal,
): Promise<any> {
  return callAPI(url, { method: 'POST', body, auth, signal });
}

export const getUpcoming = async (
  region: string,
  signal?: AbortSignal,
): Promise<any> => {
  const url = `${base_api}/upcoming/${region ?? ''}`;
  const movies = await get(url, true, signal);
  return movies;
};

export const getRatingsOrWatchlist = async (
  username: string,
  getRatings: boolean = true,
  signal?: AbortSignal,
): Promise<any> => {
  const url = `${base_api}/u/${username}/${
    getRatings ? 'ratings' : 'watchlist'
  }`;
  const movies = await get(url, true, signal);
  return movies;
};

export const search = async (
  query: string,
  signal?: AbortSignal,
): Promise<any> => {
  if (!query || query.trim().length === 0) {
    throw new Error('Search query cannot be empty');
  }
  const url = `${base_api}/search/${encodeURIComponent(query)}`;
  return get(url, true, signal);
};

export const getAllUsers = async (): Promise<any> => {
  const url = `${base_api}/users/`;
  return get(url);
};

export const getLatestActivity = async (signal?: AbortSignal): Promise<any> => {
  const url = `${base_api}/activity/`;
  return get(url, true, signal);
};

export const saveRating = async (
  rating_info: any,
  signal?: AbortSignal,
): Promise<any> => {
  if (!rating_info || typeof rating_info !== 'object') {
    throw new Error('Invalid rating info');
  }
  const url = `${base_api}/saveRating/`;
  return post(url, rating_info, true, signal);
};

export const setWatchlist = async (watchlist_info: any): Promise<any> => {
  if (!watchlist_info || typeof watchlist_info !== 'object') {
    throw new Error('Invalid watchlist info');
  }
  const url = `${base_api}/setWatchlist/`;
  return post(url, watchlist_info);
};

export const getMoviesFromPerson = async (
  personId: string,
  signal?: AbortSignal,
): Promise<any> => {
  if (!personId) {
    throw new Error('Person ID is required');
  }
  const url = `${base_api}/p/${personId}`;
  return get(url, true, signal);
};

export const getMovieById = async (
  id: string,
  _useImdb: boolean,
  extraInfo: string,
): Promise<any> => {
  if (!id) {
    throw new Error('Movie ID is required');
  }
  const url = `${base_api}/movieById/${id}/${extraInfo}`;
  return get(url);
};

export const refreshMovie = async (id: string): Promise<any> => {
  if (!id) {
    throw new Error('Movie ID is required');
  }
  const url = `${base_api}/refreshMovie/${id}`;
  return get(url);
};

export const login = async (login_info: any): Promise<boolean> => {
  if (!login_info || !login_info.username || !login_info.password) {
    throw new Error('Username and password are required');
  }
  const url = `${base_api}/login/`;
  const value = await post(url, login_info, false);
  if (value) {
    localStorage.setItem('user', JSON.stringify(value));
    return true;
  }
  return false;
};
export const logout = async (): Promise<void> => {
  const url = `${base_api}/logout/`;
  await post(url, {}, true);
};
export const updateUserOptions = async (options: any): Promise<any> => {
  const url = `${base_api}/user/options`;
  return callAPI(url, { method: 'PUT', body: options, auth: true });
};

export const getUserOptions = async (): Promise<any> => {
  const url = `${base_api}/user/options`;
  return get(url, true);
};

export const pickMovieRecommendations = async (
  params: {
    username?: string;
    keywords?: string | null;
    externalRating?: number | null; // 0-100
    wishlistOnly?: boolean;
    external?: boolean;
    limit?: number;
  } = {},
  signal?: AbortSignal,
): Promise<any> => {
  const {
    username,
    keywords,
    externalRating,
    wishlistOnly = false,
    external = false,
    limit = 5,
  } = params;
  const qs = new URLSearchParams();
  if (keywords) qs.set('keywords', keywords);
  if (externalRating !== null && externalRating !== undefined)
    qs.set('externalRating', String(externalRating));
  qs.set('limit', String(limit));
  qs.set('wishlistOnly', wishlistOnly ? 'true' : 'false');
  if (external) qs.set('external', 'true');
  const query = qs.toString() ? `?${qs.toString()}` : '';
  const url = `${base_api}/pick-movie/${encodeURIComponent(username ?? '')}${query}`;
  return get(url, true, signal);
};
